/**
 * Päivämäärä- ja kestolaskenta.
 *
 * Kirjauspäivä on kalenteripäivä, ei aikaleima (suunnitelma 4.2). Siksi
 * päivämääriä käsitellään tässä tiedostossa aina `YYYY-MM-DD`-merkkijonoina
 * eikä Date-olioina: Date on aikaleima, ja aikaleimaksi muunnettu päivämäärä
 * vaeltaa aikavyöhykemuunnoksissa väärälle päivälle.
 *
 * Ainoa kohta, jossa aikavyöhyke on mukana, on "mikä päivä nyt on" —
 * ja se ratkaistaan aina Europe/Helsinki -vyöhykkeessä.
 */
import { APP_TIME_ZONE, MAX_MINUTES_PER_ENTRY, MINUTE_STEP } from './constants.js';
import { isHoliday } from './holidays.js';
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const MONTH_KEY_RE = /^\d{4}-\d{2}$/;
const isoDateFormatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: APP_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
});
/** Tämä päivä Suomen aikavyöhykkeellä. */
export function todayInAppZone(now = new Date()) {
    return isoDateFormatter.format(now);
}
export function isIsoDate(value) {
    if (!ISO_DATE_RE.test(value))
        return false;
    const parts = value.split('-').map(Number);
    const [y, m, d] = parts;
    if (m < 1 || m > 12 || d < 1)
        return false;
    return d <= daysInMonth(y, m);
}
export function isMonthKey(value) {
    if (!MONTH_KEY_RE.test(value))
        return false;
    const month = Number(value.slice(5, 7));
    return month >= 1 && month <= 12;
}
export function daysInMonth(year, month) {
    // Date.UTC(y, m, 0) = edellisen kuukauden viimeinen päivä, koska month on
    // nollapohjainen. Puhdasta aritmetiikkaa, ei aikavyöhykettä.
    return new Date(Date.UTC(year, month, 0)).getUTCDate();
}
export function parseIsoDate(date) {
    const [year, month, day] = date.split('-').map(Number);
    return { year, month, day };
}
export function formatIsoDate(year, month, day) {
    return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}
export function monthKeyOf(date) {
    return date.slice(0, 7);
}
export function parseMonthKey(key) {
    return { year: Number(key.slice(0, 4)), month: Number(key.slice(5, 7)) };
}
export function formatMonthKey(year, month) {
    return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}`;
}
/** Kuukauden ensimmäinen ja viimeinen päivä, molemmat mukaan luettuina. */
export function monthRange(year, month) {
    return {
        from: formatIsoDate(year, month, 1),
        to: formatIsoDate(year, month, daysInMonth(year, month)),
    };
}
/** Lisää päiviä kalenteripäivään ilman aikavyöhykevaikutusta. */
export function addDays(date, days) {
    const { year, month, day } = parseIsoDate(date);
    const shifted = new Date(Date.UTC(year, month - 1, day + days));
    return formatIsoDate(shifted.getUTCFullYear(), shifted.getUTCMonth() + 1, shifted.getUTCDate());
}
export function addMonths(key, months) {
    const { year, month } = parseMonthKey(key);
    const zeroBased = year * 12 + (month - 1) + months;
    return formatMonthKey(Math.floor(zeroBased / 12), (zeroBased % 12) + 1);
}
/** Kuinka monta kalenterikuukautta aikaväli koskettaa, päätepisteet mukaan luettuina. */
export function monthsInRange(from, to) {
    const a = parseIsoDate(from);
    const b = parseIsoDate(to);
    return (b.year - a.year) * 12 + (b.month - a.month) + 1;
}
/** Kaikki aikavälin koskettamat kuukaudet järjestyksessä. */
export function monthKeysInRange(from, to) {
    const keys = [];
    let cursor = monthKeyOf(from);
    const last = monthKeyOf(to);
    // Yläraja on turvaverkko, ei liiketoimintasääntö; varsinainen raja on
    // asetuksissa (maxReportRangeMonths).
    for (let i = 0; i < 1200; i++) {
        keys.push(cursor);
        if (cursor === last)
            break;
        cursor = addMonths(cursor, 1);
    }
    return keys;
}
/** Viikonpäivä ISO-numerona: 1 = maanantai … 7 = sunnuntai. */
export function isoWeekday(date) {
    const { year, month, day } = parseIsoDate(date);
    const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
    return weekday === 0 ? 7 : weekday;
}
/** Viikon maanantai. */
export function startOfIsoWeek(date) {
    return addDays(date, -(isoWeekday(date) - 1));
}
/** Viikon sunnuntai. */
export function endOfIsoWeek(date) {
    return addDays(startOfIsoWeek(date), 6);
}
/**
 * ISO 8601 -viikkonumero. Suomalaisessa työympäristössä viikkoon viitataan
 * numerolla, joten tämä näkyy kalenterissa, raporteissa ja vienneissä.
 */
export function isoWeekNumber(date) {
    const { year, month, day } = parseIsoDate(date);
    const target = new Date(Date.UTC(year, month - 1, day));
    // Siirry viikon torstaihin: ISO-viikko kuuluu sille vuodelle, jossa sen
    // torstai on.
    const dayNumber = target.getUTCDay() === 0 ? 7 : target.getUTCDay();
    target.setUTCDate(target.getUTCDate() + 4 - dayNumber);
    const yearStart = Date.UTC(target.getUTCFullYear(), 0, 1);
    return Math.ceil(((target.getTime() - yearStart) / 86_400_000 + 1) / 7);
}
/** ISO-viikkovuosi — eri kuin kalenterivuosi vuodenvaihteen tienoilla. */
export function isoWeekYear(date) {
    const { year, month, day } = parseIsoDate(date);
    const target = new Date(Date.UTC(year, month - 1, day));
    const dayNumber = target.getUTCDay() === 0 ? 7 : target.getUTCDay();
    target.setUTCDate(target.getUTCDate() + 4 - dayNumber);
    return target.getUTCFullYear();
}
// --- Kestot ----------------------------------------------------------------
/**
 * Kelpaako kesto: positiivinen, enintään vuorokausi ja puolen tunnin
 * monikerta. Sama sääntö on tietokantarajoitteena (suunnitelma 4.1) — tämä
 * on käyttäjälle näkyvä puoli samasta säännöstä, ei sen korvike.
 */
export function isValidMinutes(minutes) {
    return (Number.isInteger(minutes) &&
        minutes > 0 &&
        minutes <= MAX_MINUTES_PER_ENTRY &&
        minutes % MINUTE_STEP === 0);
}
/** Minuutit tunneiksi. Aina puolikkaita, joten liukuluku on tässä eksakti. */
export function minutesToHours(minutes) {
    return minutes / 60;
}
export function hoursToMinutes(hours) {
    return Math.round(hours * 60);
}
/**
 * Aikaleima Suomen vyöhykkeellä ja näkyvällä siirtymällä
 * (`2026-08-17T09:12:04+03:00`).
 *
 * Vienti on kirjanpitoaineistoa, jota luetaan vuosia myöhemmin. `Z`-muoto
 * olisi oikein mutta pakottaisi lukijan tekemään muunnoksen päässään;
 * näkyvä siirtymä kertoo suoraan, mihin kellonaikaan rivi viittaa, eikä
 * hukkaa tietoa kuten paikallinen aika ilman siirtymää.
 */
export function toAppZoneIso(value) {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: APP_TIME_ZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    }).formatToParts(value);
    const get = (type) => parts.find((part) => part.type === type)?.value ?? '00';
    const offsetName = new Intl.DateTimeFormat('en-US', { timeZone: APP_TIME_ZONE, timeZoneName: 'longOffset' })
        .formatToParts(value)
        .find((part) => part.type === 'timeZoneName')?.value ?? 'GMT+00:00';
    // "GMT+03:00" → "+03:00"; talviaikaan "GMT+02:00".
    const offset = offsetName.replace('GMT', '') || '+00:00';
    return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}:${get('second')}${offset}`;
}
/**
 * Arkipäivät aikavälillä, päätepisteet mukaan luettuina.
 *
 * **Kiinteät pyhäpäivät vähennetään** (backlog 2.12): joulupäivä ei ole
 * työpäivä, eikä odotettu työaika saa väittää toisin. Tämä on odotusarvon
 * nimittäjä laskutusasteessa (12.6), lukituksen tarkistuslistassa (6.3) ja
 * kalenterin täyttöasteessa, joten korjaus osuu kaikkiin kolmeen kerralla —
 * eikä yksikään niistä voi eriytyä toisesta, koska määritelmä on yksi.
 *
 * Liikkuvat pyhät (pääsiäinen, helatorstai, juhannusaatto) eivät ole vielä
 * mukana; ks. `holidays.ts`.
 */
export function businessDaysBetween(from, to) {
    let count = 0;
    for (let cursor = from; cursor <= to; cursor = addDays(cursor, 1)) {
        if (isoWeekday(cursor) <= 5 && !isHoliday(cursor))
            count++;
    }
    return count;
}
const TIME_OF_DAY_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;
export function isTimeOfDay(value) {
    return TIME_OF_DAY_RE.test(value);
}
const wallClockFormatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: APP_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
});
/** Hetki Suomen seinäkellona: `2026-09-16T09:00`. */
function wallClockInAppZone(instant) {
    const parts = wallClockFormatter.formatToParts(instant);
    const get = (type) => parts.find((part) => part.type === type)?.value ?? '00';
    return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
}
/** Vyöhykkeen siirtymä minuutteina itään UTC:stä annetulla hetkellä. */
function appZoneOffsetMinutes(instant) {
    const [date, time] = wallClockInAppZone(instant).split('T');
    const { year, month, day } = parseIsoDate(date);
    const [hours, minutes] = time.split(':').map(Number);
    return Math.round((Date.UTC(year, month - 1, day, hours, minutes) - instant.getTime()) / 60_000);
}
/**
 * Suomen seinäkello (päivä + kellonaika) hetkeksi.
 *
 * Käänteinen `toAppZoneIso`lle ja tehtävämuistutuksen ainoa
 * aikavyöhykekohta: käyttäjä sanoo "16.9. klo 9", kanta tallentaa hetken.
 * Naiivi `new Date('2026-09-16T09:00')` lukisi ajan **palvelimen**
 * vyöhykkeellä, joka on tuotannossa UTC — muistutus lähtisi kesällä kolme
 * tuntia myöhässä.
 *
 * Kellonsiirto tekee kahdesta yöstä vuodessa erikoisia, ja molemmat on
 * ratkaistava jotenkin:
 *
 *   - **Kevät** (29.3.2026): klo 03.30 ei ole olemassa lainkaan. Tulos on
 *     seuraava kelpaava hetki, eli klo 04.30 — muistutus myöhästyy tunnin
 *     eikä katoa.
 *   - **Syksy** (25.10.2026): klo 03.30 on kahdesti. Tulos on
 *     ensimmäinen, koska myöhässä oleva muistutus on parempi kuin
 *     etuajassa oleva.
 *
 * Ehdokkaat haetaan probaamalla vuorokausi molempiin suuntiin, koska
 * siirtymä voi olla kummalla puolen tahansa annettua kellonaikaa.
 */
export function appZoneInstant(date, time) {
    const { year, month, day } = parseIsoDate(date);
    const [hours, minutes] = time.split(':').map(Number);
    const naive = Date.UTC(year, month - 1, day, hours, minutes);
    const day12h = 12 * 60 * 60_000;
    // Suurin siirtymä ensin: se antaa aikaisimman hetken, ja kahdesti
    // esiintyvästä kellonajasta halutaan aikaisempi.
    const offsets = [
        ...new Set([
            appZoneOffsetMinutes(new Date(naive - day12h)),
            appZoneOffsetMinutes(new Date(naive + day12h)),
        ]),
    ].sort((a, b) => b - a);
    for (const offset of offsets) {
        const instant = new Date(naive - offset * 60_000);
        if (wallClockInAppZone(instant) === `${date}T${time}`)
            return instant;
    }
    // Kevään aukko: mikään ehdokas ei osu, koska kellonaikaa ei ole.
    // Pienin siirtymä siirtää hetken aukon yli eteenpäin.
    return new Date(naive - Math.min(...offsets) * 60_000);
}
//# sourceMappingURL=time.js.map